import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { ConceptGroup } from '@verantos-dev/types-concepts';

interface DetailsHeaderProps {
  conceptGroupDetails?: ConceptGroup;
  isConceptGroupDetailsLoading: boolean;
  exporting: boolean;
  onExportAll: (() => Promise<void>) | undefined;
}

export default function DetailsHeader({
  conceptGroupDetails,
  isConceptGroupDetailsLoading,
  exporting,
  onExportAll,
}: DetailsHeaderProps) {
  const intl = useIntl();

  return (
    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
      <Stack direction={'row'} gap={4}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MapsHomeWorkOutlinedIcon sx={{ mr: 1, height: '16px', width: '16px' }} />
          <Typography color={theme.palette.text.secondary} variant="body2" marginRight={1}>
            {intl.formatMessage({ id: 'conceptGroup.details.domains' })}
          </Typography>
          <Typography color={theme.palette.text.primary} variant="body2">
            {isConceptGroupDetailsLoading ? (
              <Skeleton variant="text" height={20} width={100} />
            ) : (
              conceptGroupDetails?.domains
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountCircleIcon sx={{ mr: 1, height: '16px', width: '16px' }} />
          <Typography color={theme.palette.text.secondary} variant="body2" marginRight={1}>
            {intl.formatMessage({ id: 'conceptGroup.details.ownedBy' })}
          </Typography>
          <Typography color={theme.palette.text.primary} variant="body2">
            {isConceptGroupDetailsLoading ? (
              <Skeleton variant="text" height={20} width={100} />
            ) : (
              conceptGroupDetails?.owner
            )}
          </Typography>
        </Box>

        {conceptGroupDetails?.modifiedBy?.transactionDate && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 1, height: '16px', width: '16px' }} />
            <Typography color={theme.palette.text.secondary} variant="body2" marginRight={1}>
              {intl.formatMessage({ id: 'conceptGroup.details.lastModified' })}
            </Typography>
            <Typography color={theme.palette.text.primary} variant="body2">
              {formatDate(conceptGroupDetails?.modifiedBy?.transactionDate, 'dateTimeFull')}
            </Typography>
          </Box>
        )}
      </Stack>
      {onExportAll && (
        <Icon
          id="download-concept-group-csv"
          sx={{
            cursor: exporting ? 'progress' : 'pointer',
            opacity: exporting ? 0.5 : 0.65,
            margin: 1,
            color: theme.palette.action.active,
          }}
          aria-label="download"
          icon="download"
          onClick={exporting ? undefined : onExportAll}
        />
      )}
    </Stack>
  );
}
